/* ============================================================================
   ACEPLORE · module-recently-viewed.js — renders the shopper's recently viewed
   products from localStorage ('lumina:recent', written by module-product.js).
   Custom element: <lumina-recently-viewed>.

   For each stored handle (excluding the current product, passed via
   data-current) it fetches the product JSON at
   `${config.routes.root}products/${handle}.js` and renders a minimal,
   client-side card. The whole section hides itself if fewer than 2 cards render.
   ========================================================================== */
(function () {
  'use strict';

  if (!window.Lumina || !window.Lumina.Element) return;

  var Lumina = window.Lumina;
  var utils = Lumina.utils;
  var config = Lumina.config;
  var STORAGE_KEY = 'lumina:recent';

  // Normalize routes.root so we always have exactly one trailing slash.
  function rootUrl() {
    var root = (config.routes && config.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  // Append a Shopify CDN width param without clobbering existing query strings.
  function withWidth(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  class RecentlyViewed extends Lumina.Element {
    connectedCallback() {
      this.grid = this.querySelector('[data-recently-viewed-grid]');
      this.current = (this.dataset.current || '').trim();
      this.max = parseInt(this.dataset.max || '0', 10) || 8;
      this.rendered = 0;

      var handles = this.readHandles();
      if (!handles.length) {
        this.hide();
        return;
      }
      // Bottom-of-page section: defer the up-to-8 product fetches until the
      // shopper actually scrolls near it.
      if ('IntersectionObserver' in window) {
        var self = this;
        // Observe a visible ancestor (the section wrapper): `this` starts
        // [hidden] (display:none) so it generates no box and would never
        // intersect. render() still reveals `this` / hide() re-hides it.
        var target = this.closest('[data-section-type="recently-viewed"]') || this.parentElement || this;
        this.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting) return;
            self.io.disconnect();
            self.io = null;
            self.render(handles);
          });
        }, { rootMargin: '600px 0px' });
        this.io.observe(target);
      } else {
        this.render(handles);
      }
    }

    disconnectedCallback() {
      if (this.io) { this.io.disconnect(); this.io = null; }
      if (super.disconnectedCallback) super.disconnectedCallback();
    }

    // Read handles (newest first), drop the current product + any dupes/blanks.
    readHandles() {
      var list = utils.storage.get(STORAGE_KEY, []);
      if (!Array.isArray(list)) return [];
      var seen = Object.create(null);
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var h = typeof list[i] === 'string' ? list[i].trim() : '';
        if (!h || h === this.current || seen[h]) continue;
        seen[h] = true;
        out.push(h);
        if (out.length >= this.max) break;
      }
      return out;
    }

    async render(handles) {
      if (!this.grid) {
        this.hide();
        return;
      }
      var root = rootUrl();

      // Fetch all product JSON in parallel; tolerate individual failures.
      var results = await Promise.all(
        handles.map(function (handle) {
          return utils
            .fetchJSON(root + 'products/' + encodeURIComponent(handle) + '.js')
            .then(function (data) { return data; })
            .catch(function () { return null; });
        })
      );

      // If the element was removed (e.g. theme editor reload) bail.
      if (!this.isConnected) return;

      var frag = document.createDocumentFragment();
      for (var i = 0; i < results.length; i++) {
        var card = this.buildCard(results[i]);
        if (card) {
          frag.appendChild(card);
          this.rendered++;
        }
      }

      // Require at least 2 valid cards, otherwise the section is noise.
      if (this.rendered < 2) {
        this.hide();
        return;
      }

      this.grid.textContent = '';
      this.grid.appendChild(frag);
      this.removeAttribute('hidden');

      // Animate freshly inserted cards via the shared reveal system — with a
      // hard fallback so cards can never stay invisible.
      if (Lumina.initReveal) Lumina.initReveal(this.grid);
      else this.grid.querySelectorAll('.lm-reveal').forEach(function (el) { el.classList.add('is-visible'); });
    }

    buildCard(product) {
      if (!product || !product.handle || !product.url) return null;

      var card = document.createElement('div');
      card.className = 'lm-rv-card lm-reveal';

      // --- Media (linked) ---
      var mediaLink = document.createElement('a');
      mediaLink.className = 'lm-rv-card__media';
      mediaLink.href = product.url;
      mediaLink.setAttribute('tabindex', '-1');
      mediaLink.setAttribute('aria-hidden', 'true');

      var imgSrc = product.featured_image || (product.images && product.images[0]) || '';
      if (imgSrc) {
        var img = document.createElement('img');
        img.className = 'lm-rv-card__img';
        img.src = withWidth(imgSrc, 400);
        img.alt = '';
        img.width = 400;
        img.height = 400;
        img.loading = 'lazy';
        img.decoding = 'async';
        mediaLink.appendChild(img);
      } else {
        var ph = document.createElement('span');
        ph.className = 'lm-rv-card__placeholder';
        ph.setAttribute('aria-hidden', 'true');
        mediaLink.appendChild(ph);
      }
      card.appendChild(mediaLink);

      // --- Body ---
      var body = document.createElement('div');
      body.className = 'lm-rv-card__body';

      var title = document.createElement('h3');
      title.className = 'lm-rv-card__title';
      var titleLink = document.createElement('a');
      titleLink.href = product.url;
      // textContent — never trust product titles to be HTML-safe.
      titleLink.textContent = product.title || '';
      title.appendChild(titleLink);
      body.appendChild(title);

      var price = document.createElement('span');
      price.className = 'lm-rv-card__price';
      price.textContent = utils.formatMoney(product.price);
      body.appendChild(price);

      card.appendChild(body);
      return card;
    }

    hide() {
      this.setAttribute('hidden', '');
    }
  }

  if (!customElements.get('lumina-recently-viewed')) {
    customElements.define('lumina-recently-viewed', RecentlyViewed);
  }
})();
